import {ID} from '@wandb/weave/core/util/id';
import React, {useRef} from 'react';
import {twMerge} from 'tailwind-merge';

export type TemplateSection = {
  rows: {
    groupTitle?: string;
    label: string;
    currDisplayValue: string | undefined;
    newDisplayValue?: string;
  }[];
};

export type WorkspaceTemplateDisplayValuesProps = {
  /**
   * templateSections contains groups of settings within that category
   */
  templateSections: TemplateSection[];
  /**
   * templateCategory represents the primary template classification (e.g. "Line plots")
   */
  templateCategory?: string;
};

export const WorkspaceTemplateDisplayValues = ({
  templateSections,
  templateCategory,
}: WorkspaceTemplateDisplayValuesProps) => {
  // Generate stable and unique IDs
  const ref = useRef({
    templateCategoryId: 'template-category-' + ID(),
  });
  return (
    <section
      className="mt-8 rounded bg-moon-300/[.24] p-16"
      aria-labelledby={ref.current.templateCategoryId}>
      {templateCategory && (
        <h5
          id={ref.current.templateCategoryId}
          className="mb-4 text-sm font-semibold uppercase text-moon-800">
          {templateCategory}
        </h5>
      )}
      <div className="flex w-full flex-col gap-8 text-sm text-moon-800">
        {templateSections.map((section, index) => {
          // Group rows by their groupTitle
          const groupedRows = section.rows.reduce<
            Record<string, typeof section.rows>
          >((acc, row) => {
            const groupKey = row.groupTitle ?? '';
            if (!acc[groupKey]) {
              acc[groupKey] = [];
            }
            acc[groupKey].push(row);
            return acc;
          }, {});
          const sectionKey = `${ref.current.templateCategoryId}-${index}`;
          return (
            <section key={sectionKey}>
              <dl role="list">
                {Object.entries(groupedRows).map(([groupTitle, rows]) => (
                  <div
                    key={groupTitle}
                    className="mb-8"
                    role="region"
                    aria-label={groupTitle || undefined}>
                    {groupTitle && (
                      <h6 className="mb-4 text-sm font-semibold text-moon-800">
                        {groupTitle}
                      </h6>
                    )}
                    {rows
                      .filter(
                        row =>
                          row.currDisplayValue != null ||
                          row.newDisplayValue != null
                      )
                      .map((row, i) => {
                        const rowKey = `row-${i}-${groupTitle}`;
                        return (
                          <div key={rowKey} className="mb-4">
                            <DisplayRow
                              label={row.label}
                              currDisplayValue={row.currDisplayValue}
                              newDisplayValue={row.newDisplayValue}
                            />
                          </div>
                        );
                      })}
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>
    </section>
  );
};

const DisplayRow = ({
  label,
  currDisplayValue,
  newDisplayValue,
}: {
  label: string;
  currDisplayValue: string | undefined;
  newDisplayValue: string | undefined;
}) => {
  // Generate stable and unique IDs
  const ref = useRef({
    labelId: 'description-label-' + ID(),
  });
  const isValueModified =
    newDisplayValue != null && newDisplayValue !== currDisplayValue;

  return (
    <div className="flex text-moon-500" role="listitem">
      <dt className="w-1/3" id={ref.current.labelId}>
        {label}
      </dt>
      <span className="w-16">:</span>
      <dd className="m-0" aria-labelledby={ref.current.labelId}>
        {currDisplayValue && (
          <span
            className={twMerge(isValueModified && 'line-through')}
            aria-label="Existing template value">
            {currDisplayValue}
          </span>
        )}
        {isValueModified && (
          <span
            className={twMerge(currDisplayValue != null && 'ml-4')}
            aria-label="Updated template value">
            {newDisplayValue}
          </span>
        )}
      </dd>
    </div>
  );
};
